import { ColumnDetails, ColumnType } from '../../../../grid/types/common';
import _ from 'lodash';
import { DateConditions, IntEnumConditions } from '../../../../grid/gridFilters/types';
import { ViewConfiguration } from '../../../../grid/gridView/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { RELATIVE_TIME_RANGES } from '@nirvana/shared/constants';
import {
  getIntegrityMonitorSeverityValues,
  getIntegrityMonitorTypeValues,
  IntegrityMonitorEntityType,
  IntegrityMonitorRulesActionResults,
  IntegrityMonitorRulesActionValues,
  IntegrityMonitorSeverity,
} from '../../../common/utils/types';
import {
  IntegrityMonitorAttributeChangesStartType,
  IntegrityMonitorAttributeChangesStatus,
} from '../../integrityMonitorEventsDetailsPanel/types';

export enum Column {
  INTEGRITY_MONITOR_EVENT_DATE,
  INTEGRITY_MONITOR_ENDPOINT,
  INTEGRITY_MONITOR_REASON,
  INTEGRITY_MONITOR_CATEGORY,
  INTEGRITY_MONITOR_CHANGE,
  INTEGRITY_MONITOR_SEVERITY,
  INTEGRITY_MONITOR_TYPE,
  INTEGRITY_MONITOR_LOCATION,
  INTEGRITY_MONITOR_USER,
  INTEGRITY_MONITOR_ATTRIBUTE_CHANGES,
  INTEGRITY_MONITOR_COMPANY,
}

export enum ColumnKeys {
  INTEGRITY_MONITOR_EVENT_DATE = 'event_time',
  INTEGRITY_MONITOR_ENDPOINT = 'endpoint_name',
  INTEGRITY_MONITOR_REASON = 'rule_name',
  INTEGRITY_MONITOR_CATEGORY = 'category',
  INTEGRITY_MONITOR_CHANGE = 'change_type',
  INTEGRITY_MONITOR_SEVERITY = 'severity',
  INTEGRITY_MONITOR_TYPE = 'entity_type',
  INTEGRITY_MONITOR_LOCATION = 'key',
  INTEGRITY_MONITOR_USER = 'user',
  INTEGRITY_MONITOR_ATTRIBUTE_CHANGES = 'changes',
  INTEGRITY_MONITOR_COMPANY = 'company_name',
}

export const DISPLAYED_FILTERS = [
  {
    [ColumnKeys.INTEGRITY_MONITOR_EVENT_DATE]: {
      timeRange: {
        kind: TimeRangeKind.RELATIVE_TIME_RANGE,
        relative: RELATIVE_TIME_RANGES.LAST_24_HOURS,
      },
    },
  },
  { [ColumnKeys.INTEGRITY_MONITOR_ENDPOINT]: { contains: '' } },
  { [ColumnKeys.INTEGRITY_MONITOR_CHANGE]: { in: [] } },
  { [ColumnKeys.INTEGRITY_MONITOR_SEVERITY]: { in: [] } },
  { [ColumnKeys.INTEGRITY_MONITOR_CATEGORY]: { in: [] } },
];

export const PREDEFINED_FILTERS_KEYS = [
  ColumnKeys.INTEGRITY_MONITOR_EVENT_DATE,
  ColumnKeys.INTEGRITY_MONITOR_ENDPOINT,
  ColumnKeys.INTEGRITY_MONITOR_CHANGE,
  ColumnKeys.INTEGRITY_MONITOR_SEVERITY,
  ColumnKeys.INTEGRITY_MONITOR_CATEGORY,
];

export const DISPLAYED_COLUMNS = _.range(Object.keys(Column).length / 2);

export enum IntegrityMonitorChangeType {
  CREATED = 1,
  UPDATED,
  DELETED,
  RENAMED,
}

export enum IntegrityMonitorCategories {
  APPROVED = 'approved',
  UNAPPROVED = 'unapproved',
  BD_TRUSTED = 'bd_trusted',
}

export function getIntegrityMonitorChangeValues(translateFn) {
  return [
    {
      label: translateFn('Created'),
      value: IntegrityMonitorChangeType.CREATED,
    },
    {
      label: translateFn('Updated'),
      value: IntegrityMonitorChangeType.UPDATED,
    },
    {
      label: translateFn('Deleted'),
      value: IntegrityMonitorChangeType.DELETED,
    },
    {
      label: translateFn('Renamed'),
      value: IntegrityMonitorChangeType.RENAMED,
    },
  ];
}

export function getIntegrityMonitorCategory(translateFn) {
  return [
    {
      label: translateFn('Approved'),
      value: IntegrityMonitorCategories.APPROVED,
    },
    {
      label: translateFn('Unapproved'),
      value: IntegrityMonitorCategories.UNAPPROVED,
    },
    {
      label: translateFn('Bitdefender trusted'),
      value: IntegrityMonitorCategories.BD_TRUSTED,
    },
  ];
}

export function getColumnDetails(translateFn) {
  return [
    {
      column: Column.INTEGRITY_MONITOR_EVENT_DATE,
      key: ColumnKeys.INTEGRITY_MONITOR_EVENT_DATE,
      columnName: translateFn('Event date'),
      type: ColumnType.DATE,
      removable: false,
      sortable: true,
      editingDisabled: true,
      filterResetValue: {
        timeRange: {
          kind: TimeRangeKind.RELATIVE_TIME_RANGE,
          relative: RELATIVE_TIME_RANGES.LAST_24_HOURS,
        },
      },
    },
    {
      column: Column.INTEGRITY_MONITOR_ENDPOINT,
      key: ColumnKeys.INTEGRITY_MONITOR_ENDPOINT,
      columnName: translateFn('Endpoint'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_REASON,
      key: ColumnKeys.INTEGRITY_MONITOR_REASON,
      columnName: translateFn('Reason'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_CATEGORY,
      key: ColumnKeys.INTEGRITY_MONITOR_CATEGORY,
      columnName: translateFn('Category'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorCategory(translateFn),
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_CHANGE,
      key: ColumnKeys.INTEGRITY_MONITOR_CHANGE,
      columnName: translateFn('Change'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorChangeValues(translateFn),
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_SEVERITY,
      key: ColumnKeys.INTEGRITY_MONITOR_SEVERITY,
      columnName: translateFn('Severity'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorSeverityValues(translateFn),
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_TYPE,
      key: ColumnKeys.INTEGRITY_MONITOR_TYPE,
      columnName: translateFn('Entity type'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorTypeValues(translateFn),
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_LOCATION,
      key: ColumnKeys.INTEGRITY_MONITOR_LOCATION,
      columnName: translateFn('Location'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_USER,
      key: ColumnKeys.INTEGRITY_MONITOR_USER,
      columnName: translateFn('User'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_ATTRIBUTE_CHANGES,
      key: ColumnKeys.INTEGRITY_MONITOR_ATTRIBUTE_CHANGES,
      columnName: translateFn('Attribute changes'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      editingDisabled: true,
    },
    {
      column: Column.INTEGRITY_MONITOR_COMPANY,
      key: ColumnKeys.INTEGRITY_MONITOR_COMPANY,
      columnName: translateFn('Company'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      editingDisabled: true,
    },
  ];
}

export interface IntegrityMonitorEventsWebComponentSchema {
  views: Array<ViewConfiguration<Column, ColumnKeys>>;
  allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  defaultViewProps: ViewConfiguration<Column, ColumnKeys>;
}

export interface IntegrityMonitorChangedAttribute<T> {
  old: T;
  new: T;
}

export interface IntegrityMonitorEventsChanges {
  last_modified?: IntegrityMonitorChangedAttribute<number>;
  permissions?: IntegrityMonitorChangedAttribute<string>;
  owner?: IntegrityMonitorChangedAttribute<string>;
  group?: IntegrityMonitorChangedAttribute<string>;
  attributes?: IntegrityMonitorChangedAttribute<number>;
  hash?: IntegrityMonitorChangedAttribute<string>;
  size?: IntegrityMonitorChangedAttribute<number>;
  subkeys?: IntegrityMonitorChangedAttribute<string[]>;
  values?: IntegrityMonitorChangedAttribute<string[]>;
  publisher?: IntegrityMonitorChangedAttribute<string>;
  version?: IntegrityMonitorChangedAttribute<string>;
  installed_date?: IntegrityMonitorChangedAttribute<string>;
  install_location?: IntegrityMonitorChangedAttribute<string>;
  image_path?: IntegrityMonitorChangedAttribute<string>;
  start_type?: IntegrityMonitorChangedAttribute<IntegrityMonitorAttributeChangesStartType>;
  status?: IntegrityMonitorChangedAttribute<IntegrityMonitorAttributeChangesStatus>;
}

export enum ImEventsChangesAttributes {
  LAST_MODIFIED = 'last_modified',
  PERMISSIONS = 'permissions',
  OWNER = 'owner',
  GROUP = 'group',
  ATTRIBUTES = 'attributes',
  HASH = 'hash',
  SIZE = 'size',
  SUBKEYS = 'subkeys',
  VALUES = 'values',
  PUBLISHER = 'publisher',
  VERSION = 'version',
  INSTALLED_DATE = 'installed_date',
  INSTALL_LOCATION = 'install_location',
  IMAGE_PATH = 'image_path',
  START_TYPE = 'start_type',
  STATUS = 'status',
}

export interface IntegrityMonitorEventsActionsTaken {
  attribute: ImEventsChangesAttributes;
  action: IntegrityMonitorRulesActionValues;
  result: IntegrityMonitorRulesActionResults;
}

export function getIntegrityMonitorChangesNames(translateFn) {
  return {
    [ImEventsChangesAttributes.LAST_MODIFIED]: translateFn('last modified'),
    [ImEventsChangesAttributes.PERMISSIONS]: translateFn('permissions'),
    [ImEventsChangesAttributes.OWNER]: translateFn('owner'),
    [ImEventsChangesAttributes.GROUP]: translateFn('group'),
    [ImEventsChangesAttributes.ATTRIBUTES]: translateFn('attributes'),
    [ImEventsChangesAttributes.HASH]: translateFn('hash'),
    [ImEventsChangesAttributes.SIZE]: translateFn('size'),
    [ImEventsChangesAttributes.SUBKEYS]: translateFn('subkeys'),
    [ImEventsChangesAttributes.VALUES]: translateFn('values'),
    [ImEventsChangesAttributes.PUBLISHER]: translateFn('publisher'),
    [ImEventsChangesAttributes.VERSION]: translateFn('version'),
    [ImEventsChangesAttributes.INSTALLED_DATE]: translateFn('installed date'),
    [ImEventsChangesAttributes.INSTALL_LOCATION]: translateFn('install location'),
    [ImEventsChangesAttributes.IMAGE_PATH]: translateFn('image path'),
    [ImEventsChangesAttributes.START_TYPE]: translateFn('start type'),
    [ImEventsChangesAttributes.STATUS]: translateFn('status'),
  };
}

export function getIntegrityMonitorResultTexts(translateFn) {
  return {
    [IntegrityMonitorRulesActionResults.SUCCESSFUL]: translateFn('Successful'),
    [IntegrityMonitorRulesActionResults.FAILED]: translateFn('Failed'),
    [IntegrityMonitorRulesActionResults.PENDING]: translateFn('Pending'),
  };
}

export enum GridActions {
  APPROVE_EVENT = 'approved',
  UNAPPROVE_EVENT = 'unapproved',
}

export interface IntegrityMonitorEventsGridItem {
  _id: string;
  event_time: number;
  endpoint_id: string;
  endpoint_name: string;
  hostname: string;
  rule_name: string;
  change_type: IntegrityMonitorChangeType;
  severity: IntegrityMonitorSeverity;
  entity_type: IntegrityMonitorEntityType;
  key: string;
  user: string;
  details: string;
  category: string;
  changes: IntegrityMonitorEventsChanges;
  actions_result: Array<IntegrityMonitorEventsActionsTaken>;
  company_id: string;
  company_name: string;
}

interface Filter {
  event_time?: DateConditions;
  change_type?: IntEnumConditions;
  severity?: IntEnumConditions;
}

export interface GetDataGridItemsResponse {
  rawEvents?: Array<IntegrityMonitorEventsGridItem>;
}

export interface SetFiltersRequest {
  viewId: string;
  filters: Array<Filter>;
}
